import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { billingAPI } from '../services/billingAPI';
import type { DeploymentFeatures, LocalCreditBalance, TrialInfo } from '../services/billingAPI';
import { useAuth } from './AuthContext';
import { logger } from '../lib/logger';

interface BillingContextValue {
  features: DeploymentFeatures | null;
  balance: LocalCreditBalance | null;
  trialInfo: TrialInfo | null;
  isLoading: boolean;
  isSaaS: boolean;
  isEnterprise: boolean;
  isManual: boolean;
  creditsEnabled: boolean;
  scrapingEnabled: boolean;
  platformsEnabled: boolean;
  platformAddonBooked: boolean;
  domainsEnabled: boolean;
  /** True when SaaS + scraping enabled, no platforms (Basic tier). */
  isBasicTier: boolean;
  /** True when SaaS + platforms enabled, no scraping (Platform-Only tier). */
  isPlatformsOnlyTier: boolean;
  /** True when SaaS + both scraping and platforms as add-on (Standard tier). */
  isStandardTier: boolean;
  /** Convenience: user has competitor/domain scraping capability. */
  hasCompetitorScraping: boolean;
  /** Convenience: user has platform comparison capability. */
  hasPlatforms: boolean;
  settingsProxyVisible: boolean;
  settingsApiVisible: boolean;
  billingVisible: boolean;
  refetchBalance: () => void;
}

const BillingContext = createContext<BillingContextValue | undefined>(undefined);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { user, apiKey } = useAuth();
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const [features, setFeatures] = useState<DeploymentFeatures | null>(null);
  const [balance, setBalance] = useState<LocalCreditBalance | null>(null);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!apiKey) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    billingAPI
      .getDeploymentFeatures()
      .then(setFeatures)
      .catch((err: unknown) => {
        logger.warn('BillingContext: failed to load features', err);
        setFeatures(null);
      })
      .finally(() => setIsLoading(false));
  }, [apiKey]);

  const fetchBalance = useCallback(() => {
    if (!apiKey || !isAdmin) return;
    billingAPI
      .getLocalCreditBalance()
      .then(setBalance)
      .catch((err: unknown) => {
        logger.warn('BillingContext: failed to load balance', err);
        setBalance(null);
      });
    billingAPI
      .getOverview()
      .then((overview) => setTrialInfo(overview.trial_info ?? null))
      .catch(() => setTrialInfo(null));
  }, [apiKey, isAdmin]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Computed flags — safe defaults lean toward showing everything
  // so that enterprise/manual installs without a features response are fully functional.
  const isSaaS = features?.deployment_type === 'saas';
  const isEnterprise = features?.deployment_type === 'enterprise' || features?.deployment_type === 'manual';
  const isManual = features?.deployment_type === 'manual';
  const creditsEnabled = features?.credits_enabled ?? false;
  const scrapingEnabled = features?.scraping_enabled ?? true;
  const platformsEnabled = features?.platforms_enabled ?? true;
  const platformAddonBooked = features?.platform_addon_booked ?? true;
  const domainsEnabled = features?.domains_enabled ?? true;

  // Tier detection — only meaningful for SaaS deployments.
  // Enterprise/manual always have everything; these flags are ignored there.
  const isBasicTier = isSaaS && scrapingEnabled && !platformsEnabled;
  const isPlatformsOnlyTier = isSaaS && !scrapingEnabled && platformsEnabled;
  const isStandardTier = isSaaS && scrapingEnabled && platformsEnabled && platformAddonBooked;

  // Convenience booleans for UI gating.
  const hasCompetitorScraping = scrapingEnabled;
  const hasPlatforms = platformsEnabled;

  // Proxy / API tabs: visible by default; hidden only when feature flag explicitly says false
  const settingsProxyVisible = features?.features?.settings_proxy_visible ?? true;
  const settingsApiVisible = features?.features?.settings_api_visible ?? true;
  // Billing nav: visible by default for all admins; hidden only when feature flag explicitly says false
  const billingVisible = features?.features?.billing_visible ?? true;

  const value: BillingContextValue = {
    features,
    balance,
    trialInfo,
    isLoading,
    isSaaS,
    isEnterprise,
    isManual,
    creditsEnabled,
    scrapingEnabled,
    platformsEnabled,
    platformAddonBooked,
    domainsEnabled,
    isBasicTier,
    isPlatformsOnlyTier,
    isStandardTier,
    hasCompetitorScraping,
    hasPlatforms,
    settingsProxyVisible,
    settingsApiVisible,
    billingVisible,
    refetchBalance: fetchBalance,
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling(): BillingContextValue {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
